import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';

import {
    CalculationBox,
    CalculationBoxContainer,
    DefaultIntlFormatValues,
} from '@ratehub/base-ui';
import { SAVINGS_MONTHLY_BALANCES } from '@ratehub/inv-common';

import SavingsAccountPropType from '../definitions/SavingsAccountPropType';


function SavingsInterestCalculationSummary({
    savingsAccount,
    monthlyBalance = SAVINGS_MONTHLY_BALANCES[1],    // $5,000
}) {
    const intl = useIntl();

    const {
        bonusInterest,
        firstYearPromoReturn,
        firstYearDefaultReturn,
        firstYearReturn,
    } = savingsAccount;

    const interestRate = savingsAccount.interestRate / 100;

    // promoPeriodLines only required when there is an active promotional period active
    const promoPeriodLines = bonusInterest?.rate
        ? getPromoPeriodLines(bonusInterest)
        : null;


    return (
        <CalculationBoxContainer
            title={<FormattedMessage {...LOCAL_MESSAGES.title} />}
        >
            <Choose>
                <When condition={!bonusInterest}>
                    <CalculationBox
                        isHighlightBox
                        calculationLines={[
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.yourBalance} />,
                                definition: intl.formatNumber(monthlyBalance, { format: 'CAD0' }),
                            },
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.standardRate} />,
                                definition: intl.formatNumber(interestRate, { format: 'percent2' }),
                            },
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.totalReturn} />,
                                definition: intl.formatNumber(firstYearDefaultReturn, { format: 'CAD0' }),
                                isTotal: true,
                                isEmphasized: true,
                            },
                        ]}
                    />
                </When>

                <Otherwise>
                    <CalculationBox
                        subtitle={<FormattedMessage {...LOCAL_MESSAGES.duringPromoPeriod} />}
                        calculationLines={[
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.balance} />,
                                definition: intl.formatNumber(monthlyBalance, { format: 'CAD0' }),
                            },
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.promoRate} />,
                                definition: intl.formatNumber(bonusInterest.rate / 100, { format: 'percent2' }),
                            },
                            {
                                term: promoPeriodLines.during.term,
                                definition: promoPeriodLines.during.definition,
                            },
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.interestDuringPromoRate} />,
                                definition: intl.formatNumber(firstYearPromoReturn, { format: 'CAD0' }),
                                isEmphasized: true,
                                isTotal: true,
                            },
                        ]}
                    />

                    <CalculationBox
                        subtitle={<FormattedMessage {...LOCAL_MESSAGES.afterPromoPeriod} />}
                        calculationLines={[
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.balance} />,
                                definition: intl.formatNumber(monthlyBalance + firstYearPromoReturn, { format: 'CAD0' }),
                            },
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.standardRate} />,
                                definition: intl.formatNumber(interestRate, { format: 'percent2' }),
                            },
                            {
                                term: promoPeriodLines.after.term,
                                definition: promoPeriodLines.after.definition,
                            },
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.interestAfterPromoRate} />,
                                definition: intl.formatNumber(firstYearDefaultReturn, { format: 'CAD0' }),
                                isEmphasized: true,
                                isTotal: true,
                            },
                        ]}
                    />

                    <CalculationBox
                        isHighlightBox
                        calculationLines={[
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.addingUpTotal} />,
                                definition: (
                                    <FormattedMessage
                                        id="web-components.savings-calculation.total-summation"
                                        defaultMessage="{withPromo, number, CAD0} <operator>+</operator> {withDefault, number, CAD0}"
                                        values={{
                                            withPromo: firstYearPromoReturn,
                                            withDefault: firstYearDefaultReturn,
                                            // eslint-disable-next-line react/jsx-one-expression-per-line
                                            operator: str => <span className="operator">{str}</span>,
                                        }}
                                    />
                                ),
                            },
                            {
                                term: <FormattedMessage {...LOCAL_MESSAGES.totalReturn} />,
                                definition: intl.formatNumber(firstYearReturn, { format: 'CAD0' }),
                                isEmphasized: true,
                                isTotal: true,
                            },
                        ]}
                    />
                </Otherwise>
            </Choose>

            <p className="rh-text-xs rh-mt-0_5 rh-mb-0">
                {<FormattedMessage {...LOCAL_MESSAGES.disclaimer} />}
            </p>
        </CalculationBoxContainer>
    );
}

SavingsInterestCalculationSummary.propTypes = {
    savingsAccount: SavingsAccountPropType.isRequired,
    monthlyBalance: PropTypes.number.isRequired,
};

function getPromoPeriodLines(bonusInterest) {
    let promoExpiry = bonusInterest.expiry;

    if (promoExpiry.days) {
        const promoDays = promoExpiry.days;
        const promoRestOfYearDays = 365 - promoDays;

        return {
            during: {
                term: <FormattedMessage {...LOCAL_MESSAGES.promoPeriod} />,
                definition: (
                    <FormattedMessage
                        id="web-components.savings-calculation.promo-period.during.definition"
                        defaultMessage="{promoDays}/365 days"
                        values={{ promoDays }}
                    />
                ),
            },
            after: {
                term: <FormattedMessage {...LOCAL_MESSAGES.restOfTheYear} />,
                definition: (
                    <FormattedMessage
                        id="web-components.savings-calculation.promo-period.rest-of-year.definition"
                        defaultMessage="{promoRestOfYearDays}/365 days"
                        values={{ promoRestOfYearDays }}
                    />
                ),
            },
        };
    }

    // if we’re here, this account’s promotional period has an expiration date on a specific date

    const promoPeriodDaysUntilExpiration = dayjs(promoExpiry.date)
        .startOf('day')
        .diff(dayjs().startOf('day'), 'days');

    const promoExpirationDate = (
        <time
            dateTime={bonusInterest.expiry.date}
            suppressHydrationWarning // prevent hydration warning if server timezone is different from browser’s
        >
            <FormattedDate
                value={bonusInterest.expiry.date}
                year="numeric"
                month="short"
                day="numeric"
            />
        </time>
    );

    return {
        during: {
            term: (
                <FormattedMessage
                    id="web-components.savings-calculation.promo-period.until"
                    defaultMessage="<span>Promo period (until {promoExpirationDate})</span>"
                    values={{
                        promoExpirationDate,
                        ...DefaultIntlFormatValues,
                    }}
                />
            ),
            definition: (
                <FormattedMessage
                    id="web-components.savings-calculation.promo-period.until.definition"
                    defaultMessage="{promoPeriodDaysUntilExpiration} days"
                    values={{ promoPeriodDaysUntilExpiration }}
                />
            ),
        },
        after: {
            term: (
                <FormattedMessage
                    id="web-components.savings-calculation.promo-period.after"
                    defaultMessage="<span>After {promoExpirationDate}</span>"
                    values={{
                        promoExpirationDate,
                        ...DefaultIntlFormatValues,
                    }}
                />
            ),
            definition: (
                <FormattedMessage
                    id="web-components.savings-calculation.promo-period.after.definition"
                    defaultMessage="{promoPeriodDaysAfterExpiration} days"
                    values={{ promoPeriodDaysAfterExpiration: 365 - promoPeriodDaysUntilExpiration }}
                />
            ),
        },
    };
}


const LOCAL_MESSAGES = defineMessages({
    title: {
        id: 'web-components.savings-calculation.title',
        defaultMessage: 'How we calculated your total return',
    },
    yourBalance: {
        id: 'web-components.savings-calculation.your-balance',
        defaultMessage: 'Your balance',
    },
    standardRate: {
        id: 'web-components.savings-calculation.standard-rate',
        defaultMessage: 'Standard rate',
    },
    duringPromoPeriod: {
        id: 'web-components.savings-calculation.during-promo-period',
        defaultMessage: 'DURING PROMO PERIOD',
    },
    afterPromoPeriod: {
        id: 'web-components.savings-calculation.after-promo-period',
        defaultMessage: 'AFTER PROMO PERIOD',
    },
    balance: {
        id: 'web-components.savings-calculation.balance',
        defaultMessage: 'Balance',
    },
    promoRate: {
        id: 'web-components.savings-calculation.promo-rate',
        defaultMessage: 'Promo rate',
    },
    promoPeriod: {
        id: 'web-components.savings-calculation.promo-period',
        defaultMessage: 'Promo period',
    },
    restOfTheYear: {
        id: 'web-components.savings-calculation.rest-of-the-year',
        defaultMessage: 'Rest of the year',
    },
    interestDuringPromoRate: {
        id: 'web-components.savings-calculation.interest-during-promo-rate',
        defaultMessage: 'Interest earned with promo rate',
    },
    interestAfterPromoRate: {
        id: 'web-components.savings-calculation.interest-after-promo-rate',
        defaultMessage: 'Interest earned with standard rate',
    },
    addingUpTotal: {
        id: 'web-components.savings-calculation.adding-up-total',
        defaultMessage: 'Adding up total interest earned',
    },
    totalReturn: {
        id: 'web-components.savings-calculation.total-return',
        defaultMessage: 'Total return in one year',
    },
    disclaimer: {
        id: 'web-components.savings-calculation.disclaimer',
        defaultMessage: 'This calculation, which uses a compound interest formula, is for illustrative purposes only.',
    },
});


export default observer(SavingsInterestCalculationSummary);
